import { Component, HostListener, EventEmitter, Input, Output } from '@angular/core';
import { Toolbar } from '../tools/toolbar';
import { LevelDesignerElementBase } from '../elements/element-base';
import { LevelDesignerLockTool } from '../tools/lock-tool';
import { ParkingLevelElement } from '../elements/parking/parkinglevel';
import { PolygonElement } from '../elements/polygon-element';
import { ManagerBase, MouseEventResult } from '../managers/manager-base';
import { SelectionManager } from '../managers/selection-manager';
import { ZoomManager } from '../managers/zoom-manager';
import { RotationManager } from '../managers/rotation-manager';
import { MoveManager } from '../managers/move-manager';
import { ScaleManager } from '../managers/scale-manager';
import { PanManager } from '../managers/pan-manager';
import { ResizeManager } from '../managers/resize-manager';
import { PolygonPointsManager } from '../managers/polygon-points-manager';
import { Tool } from '../tools/tool';
import { AbstractControl, FormArray, FormControl, FormGroup } from '@angular/forms';
import { ParkingRowElement } from '../elements/parking/parkingrow';
import { ParkingSpaceElement } from '../elements/parking/parkingspace';
import { ToggleMeasurementsTool } from '../tools/toggle-measurements';
import { ToggleSnappingTool } from '../tools/toggle-snapping';
import { IslandElement } from '../elements/mapitems/objects/island';
import { ArrowElement } from '../elements/mapitems/shapes/arrow';
import { ElementToolbox } from '../elements/element-toolbox';
import { CloneTool } from '../tools/clone-tool';
import { UndoTool } from '../tools/undo-tool';
import { ToolType } from '../tools/tool';
import { PropertyEditor } from '../properties/property-editor';
import { OrganizationsService } from 'src/app/Services/organizations.service';
import { ApiService } from 'src/app/Services/api.service';
import { ParkingSpaceTypesService } from 'src/app/Services/parking-space-types.service';
import { ActionTool } from '../tools/tool-base-types';
import { AlignCenterHorizontalTool, AlignLeftTool, AlignRightTool } from '../tools/alignment/horizontal-alignment-tools';
import { AlignBottomTool, AlignMiddleVerticalTool, AlignTopTool } from '../tools/alignment/vertical-alignment-tools';
import { CompressHorizontalTool, CompressVerticalTool } from '../tools/alignment/compress-tools';
import { Gate } from '../elements/access-control/gate';
import { Lane } from '../elements/access-control/lane';
import { Striping } from '../elements/mapitems/shapes/striping';
import { Wall } from '../elements/mapitems/objects/wall';
import { Stairs } from '../elements/mapitems/objects/stairs';
import { Elevator } from '../elements/mapitems/objects/elevator';
import { Ramp } from '../elements/mapitems/objects/ramp';
import { Pillar } from '../elements/mapitems/objects/pillar';
import { Barrier } from '../elements/devices/barrier';
import { Controller } from '../elements/devices/controller';
import { TrafficLight } from '../elements/devices/traffic-light';
import { GuidanceLight } from '../elements/devices/guidance-light';
import { Camera } from '../elements/devices/camera';
import { Gateway } from '../elements/devices/gateway';
import { PointElement } from '../elements/point-element';
import { CarCounter } from '../elements/devices/car-counter';
import { Sign } from '../elements/devices/sign';
import { getRegisteredElements } from '../elements/element-decorator';
import { DeleteManager } from '../managers/delete-manager';
import { ToastrService } from 'ngx-toastr';
import { ToggleMapmodeTool } from '../tools/toggle-mapmode';
import { Maptiles } from 'src/app/util/maptiles';
import { Bounds, Geo } from 'src/app/util/geo';
import { Geography } from 'src/app/util/geography';
import * as turf from '@turf/turf';
import { ValidationPanel } from '../validation/validation-panel';
import { ClipboardManager } from '../managers/clipboard-manager';
import { ParkingMeter } from '../elements/devices/parking-meter';

@Component({
    selector: 'app-level-designer',
    templateUrl: './level-designer.component.html',
    styleUrls: ['./level-designer.component.scss'],
    standalone: false
})
export class LevelDesignerComponent {
  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private toolbar: Toolbar | null = null;
  private elementToolbox: ElementToolbox | null = null;
  public elements: LevelDesignerElementBase[] = [];
  private scale: number = 1;
  private offsetX: number = 0;
  private offsetY: number = 0;
  private isPanning: boolean = false;
  private lastMouseX: number = 0;
  private lastMouseY: number = 0;
  public selectedElements: LevelDesignerElementBase[] = [];
  private isDrawingMarquee: boolean = false;
  private marqueeStartX: number = 0;
  private marqueeStartY: number = 0;
  private marqueeEndX: number = 0;
  private marqueeEndY: number = 0;
  private isDragging: boolean = false;
  private dragStartX: number = 0;
  private dragStartY: number = 0;
  private isDraggingPolygonPoint: boolean = false;
  private activePolygonElement: PolygonElement | null = null;
  private isRotating: boolean = false;
  private rotationCenterX: number = 0;
  private rotationCenterY: number = 0;
  public isShiftPressed: boolean = false;
  private rotationStartAngle: number = 0;
  private currentRotationAngle: number = 0;
  private lastSnappedAngle: number = 0;
  private lastMouseEvent: MouseEvent | null = null;
  public managers: ManagerBase[] = [];
  private activeTool: Tool | null = null;
  public selectionChanged = new EventEmitter<LevelDesignerElementBase[]>();
  // Holds one baseline snapshot (state before any action) plus up to 10 post-action
  // snapshots, so the user can undo exactly the last 10 user actions.
  private undoStack: any[] = [];
  private maxUndoStackSize: number = 11;
  private _levelData: FormGroup | null = null;
  public elementConstructors: Map<string, (formControl: FormGroup) => LevelDesignerElementBase> = new Map();
  private propertyEditor: PropertyEditor | null = null;
  public stateChanged = new EventEmitter<void>();
  @Output() levelDataChange = new EventEmitter<FormGroup>();
  public isCtrlPressed: boolean = false;
  public isAltPressed: boolean = false;
  public MapMode : boolean = true;
  private parkingLevel: ParkingLevelElement | null = null;

  // Static properties for tile caching
  private static tileCache: Map<string, HTMLImageElement> = new Map<string, HTMLImageElement>();
  private static tileLoadedStatus: Map<string, boolean> = new Map<string, boolean>();
  private static lastZoomLevel: number = 0;

  // Add these properties to the class
  private isMapDragging: boolean = false;
  private mapDragStartX: number = 0;
  private mapDragStartY: number = 0;
  private mapDragStartLongitude: number = 0;
  private mapDragStartLatitude: number = 0;
  
  // Add properties for map rotation
  private isMapRotating: boolean = false;
  private mapRotateStartX: number = 0;
  private mapRotateStartY: number = 0;
  private mapRotateStartAngle: number = 0;

  // Add property to store validation errors
  private validationErrors: Map<LevelDesignerElementBase, string[]> = new Map();

  // Add these constants near the top of the class after the existing properties
  private readonly TOOLBAR_HEIGHT: number = 40;
  private readonly TOOLBAR_GAP: number = 10;
  private readonly TOOLBAR_Y: number = 10;
  private readonly TOOLBAR_WIDTH: number = 200;

  private validationPanel: ValidationPanel | null = null;

  private clipboardManager!: ClipboardManager;

  // Add double click tracking properties
  private lastMouseDownTime: number = 0;
  private lastMouseDownX: number = 0;
  private lastMouseDownY: number = 0;
  private readonly DOUBLE_CLICK_DELAY: number = 300; // milliseconds

  @Input()
  get levelData(): FormGroup | null {
    return this._levelData;
  }
  set levelData(value: FormGroup | null) {
    this._levelData = value;
    if (value) {
      this.levelDataChange.emit(value);
    }
    this.reloadElements();
  }

  constructor(
    private organizationsService: OrganizationsService, 
    private apiService: ApiService,
    private parkingSpaceTypesService: ParkingSpaceTypesService,
    public toastService: ToastrService
  ) {
    this.MapMode = false; // Initialize MapMode to false
    this.saveState(); // Save initial state

    // Subscribe to space types loaded event
    this.parkingSpaceTypesService.onSpaceTypesLoaded.subscribe(() => {
      this.redraw();
    });

  }

  ngAfterViewInit() {
    this.initializeCanvas();
    this.addEventListeners();

    this.managers.push(new RotationManager(this, this.canvas!));
    this.managers.push(new PolygonPointsManager(this, this.canvas!));
    this.managers.push(new ScaleManager(this, this.canvas!));
    this.managers.push(new MoveManager(this, this.canvas!));
    this.managers.push(new ResizeManager(this, this.canvas!));
    this.managers.push(new SelectionManager(this, this.canvas!));
    this.managers.push(new PanManager(this, this.canvas!));
    this.managers.push(new ZoomManager(this, this.canvas!));
    this.managers.push(new DeleteManager(this, this.canvas!));
    this.clipboardManager = new ClipboardManager(this, this.canvas!);
    this.managers.push(this.clipboardManager);

    this.createToolbar();

    this.registerElementConstructors();

    this.drawElements();

    if (this.canvas) {
      this.validationPanel = new ValidationPanel(this, this.canvas);
    }
  }

  ngOnInit() {
    
  }

  private addEventListeners() {
    if (this.canvas) {
      this.canvas.addEventListener('wheel', this.handleWheel.bind(this));
      window.addEventListener('keydown', this.handleKeyDown.bind(this));
      window.addEventListener('keyup', this.handleKeyUp.bind(this));
    }
  }

  private registerElementConstructors() {
    getRegisteredElements().forEach(elementClass => {
        const instance = new elementClass(new FormGroup({}));
        this.elementConstructors.set(
            instance.TypeName.toLowerCase(),
            (formGroup: FormGroup) => new elementClass(formGroup)
        );
    });
  }

  @HostListener('mousedown', ['$event'])
  onMouseDown(event: MouseEvent) {
    if (this.isClickOnToolbar(event)) {
      this.handleToolbarClick(event);
      return;
    }
    const coords = this.getCanvasCoordinates(event);
    const worldCoords = this.getWorldCoordinates(event);
    
    if(!this.parkingLevel?.isPointInside(worldCoords.x, worldCoords.y)) {
      // Check if we're in map mode and Ctrl is pressed for map dragging
      if (this.MapMode && this.isCtrlPressed && event.button === 0) {
        this.startMapDragging(event);
        return;
      }
      
      // Check if we're in map mode and Shift is pressed for map rotation
      if (this.MapMode && this.isShiftPressed && event.button === 0) {
        this.startMapRotating(event);
        return;
      }
    }

    // Check for double click
    const currentTime = Date.now();
    if (this.isDoubleClick(coords.x, coords.y, currentTime)) {
      const result = this.handleDoubleClick(event);
      if (result?.stopPropagation) {
        return;
      }
    }

    // Update last mouse down info
    this.lastMouseDownX = coords.x;
    this.lastMouseDownY = coords.y;
    this.lastMouseDownTime = currentTime;

    let result: MouseEventResult | null | undefined;

    result = this.toolbar?.handleMouseDown(coords.x, coords.y);
    if (result) {
      this.canvas!.style.cursor = result.cursor;
      if(result.stopPropagation === true) 
        return;
    }

    // Check if click is on element toolbox first
    if (this.elementToolbox?.isPointInside(
        event.clientX - this.canvas!.getBoundingClientRect().left,
        event.clientY - this.canvas!.getBoundingClientRect().top
    )) {
        this.elementToolbox.handleMouseDown(event);
        return;
    }

    // Check if click is on property editor
    if (this.propertyEditor?.isPointInside(
        event.clientX - this.canvas!.getBoundingClientRect().left,
        event.clientY - this.canvas!.getBoundingClientRect().top
    )) {
        this.propertyEditor.handleClick(event);
        return;
    }

    if (this.validationPanel?.handleClick(event)) {
      return;
    }
    

    // Pass event to all elements' onMouseDown method
    
    for (const element of this.elements) {
      if (element.handleMouseDown(worldCoords.x, worldCoords.y)) {
        this.redraw();
        return;
      }
    }

    // Otherwise process normal mouse down logic...
    let shouldRedraw = false;

    for (const manager of this.managers) {
      if (manager.handleMouseDown) {
        const startTime = performance.now();
        const managerResult = manager.handleMouseDown(event);
        const endTime = performance.now();
        const executionTime = endTime - startTime;

       
        if (managerResult) {
          result = managerResult;
          shouldRedraw = shouldRedraw || result.redraw;
          if (result.stopPropagation === true) {
            break;
          }
        }
      }
    }

    // // Pass event to active tool using world coordinates
    // if (this.activeTool && this.activeTool instanceof ActionTool) {
    //   const worldCoords = this.getWorldCoordinates(event);
    //   (this.activeTool as ActionTool).onClick(worldCoords.x, worldCoords.y);
    // }


    if (result) {
      this.canvas!.style.cursor = result.cursor;
    } else {
      this.canvas!.style.cursor = 'default';
    }

    if (shouldRedraw) {
      this.redraw();
    }
  }

  @HostListener('mousemove', ['$event'])
  onMouseMove(event: MouseEvent) {
    // Handle map dragging if active
    if (this.isMapDragging) {
      this.updateMapDragging(event);
      this.redraw();
      return;
    }
    
    // Handle map rotation if active
    if (this.isMapRotating) {
      this.updateMapRotation(event);
      this.redraw();
      return;
    }

    const coords = this.getCanvasCoordinates(event);
    let result: MouseEventResult | undefined | null;

    result = this.toolbar?.handleMouseMove(coords.x, coords.y);
    if(result) {
      this.canvas!.style.cursor = result.cursor;
      if(result.redraw === true) 
        this.redraw();
      if(result.stopPropagation === true) 
        return;
    }

    this.lastMouseEvent = event;
    if(this.elementToolbox?.draggedElement !== null) {
      this.elementToolbox?.handleMouseMove(event);
      this.redraw();
      return;
    }
    this.elementToolbox?.handleMouseMove(event);
    if (this.isClickOnToolbar(event)) {
      const canvasRect = this.canvas!.getBoundingClientRect();
      const x = event.clientX - canvasRect.left;
      const y = event.clientY - canvasRect.top;
      this.toolbar?.handleMouseMove(x, y);
      return;
    }

    // Pass event to all elements' onMouseMove method
    const worldCoords = this.getWorldCoordinates(event);
    for (const element of this.elements) {
      if (element.handleMouseMove(worldCoords.x, worldCoords.y)) {
        this.redraw();
        return;
      }
    }

    let shouldRedraw = false;

    for (const manager of this.managers) {
      if (manager.handleMouseMove) {
        const startTime = performance.now();
        const managerResult = manager.handleMouseMove(event);
        const endTime = performance.now();
        const executionTime = endTime - startTime;


        if (managerResult) {
          result = managerResult;
          shouldRedraw = shouldRedraw || result.redraw;
          if (result.stopPropagation === true) {
            break;
          }
        }
      }
    }

    if (result) {
      this.canvas!.style.cursor = result.cursor;
    } else {
      this.canvas!.style.cursor = 'default';
    }

   

    if (shouldRedraw) {
      // Update form values for all selected elements
      // for (const element of this.selectedElements) {
      //   element.updateForm();
      // }
      this.redraw();
    }
  }

  @HostListener('mouseup', ['$event'])
  onMouseUp(event: MouseEvent) {
    // End map dragging if active
    if (this.isMapDragging) {
      this.endMapDragging();
      return;
    }
    
    // End map rotation if active
    if (this.isMapRotating) {
      this.endMapRotation();
      return;
    }

    const coords = this.getCanvasCoordinates(event);

    // Handle element toolbox first if it has a dragged element
    if (this.elementToolbox?.draggedElement) {
        this.elementToolbox.handleMouseUp(event);
        return;
    }

    // Try toolbar next
    if (this.toolbar?.handleMouseUp(coords.x, coords.y)) {
        return;
    }

    // Otherwise process normal mouse up logic...
    let result: MouseEventResult | undefined;
    let shouldRedraw = false;

    for (const manager of this.managers) {
        if (manager.handleMouseUp) {
            const startTime = performance.now();
            const managerResult = manager.handleMouseUp(event);
            const endTime = performance.now();
            const executionTime = endTime - startTime;

            if (managerResult) {
                result = managerResult;
                shouldRedraw = shouldRedraw || result.redraw;
                if (result.stopPropagation === true) {
                    break;
                }
            }
        }
    }

    // Pass event to all elements' onMouseUp method
    const worldCoords = this.getWorldCoordinates(event);
    for (const element of this.elements) {
      if (element.handleMouseUp(worldCoords.x, worldCoords.y)) {
        shouldRedraw = true;
        break;
      }
    }
    if (result) {
        this.canvas!.style.cursor = result.cursor;
    } else {
        this.canvas!.style.cursor = 'default';
    }

    if (shouldRedraw) {
        this.redraw();
    }

    if (this.elementToolbox?.draggedElement) {
        // Check position validity after drag ends
        const errors = this.elementToolbox.draggedElement.checkPositionValid(this.elements).errors;
        if (errors.length > 0) {
            // Optionally show errors to user
            console.log('Position validation errors:', errors);
        }
        this.elementToolbox.draggedElement = null;
    }
    this.isDragging = false;
    this.dragStartX = 0;
    this.dragStartY = 0;
    this.redraw();
  }

  private handleWheel(event: WheelEvent) {
    // First check if the mouse is over the toolbox
    if (this.elementToolbox?.isPointInside(event.offsetX, event.offsetY)) {
      this.elementToolbox?.handleWheel(event);
      return; // Let the toolbox handle the wheel event
    }
    // Check if wheel event is inside property editor
    if (this.propertyEditor?.isPointInside(event.offsetX, event.offsetY)) {
      this.propertyEditor?.handleWheel(event);
      return;
    }

    if (this.validationPanel?.handleWheel(event)) {
      return;
    }

    //this.canvas!.style.cursor = 'default';
    for (const manager of this.managers) {
      if (manager.handleWheel && manager.handleWheel(event) === false) {
        return;
      }
    }
  }

  private handleKeyDown(event: KeyboardEvent): void {
    // Check PropertyEditor first
    if (this.propertyEditor?.isEditing()) {
        if (this.propertyEditor.handleKeyEvent(event)) {
            return; // Event was handled by PropertyEditor
        }
    }

    switch (event.key.toLowerCase()) {
        case 'shift':
            this.isShiftPressed = true;
            break;
        case 'control':
            this.isCtrlPressed = true;
            break;
        case 'alt':
            this.isAltPressed = true;
            break;
        case 'escape':
            this.toolbar?.cancelAction();
            break;
    }
    if (event.ctrlKey && event.key === 'z') {
        this.undo();
        return;
    }

    // Only proceed with manager and tool handling if PropertyEditor didn't handle the event
    for (const manager of this.managers) {
        if (manager.handleKeyDown) {
            manager.handleKeyDown(event);
        }
    }

    if (this.activeTool) {
        this.activeTool.onKeyPress(event.key);
    }
  }

  private handleKeyUp(event: KeyboardEvent): void {
    switch (event.key.toLowerCase()) {
        case 'shift':
            this.isShiftPressed = false;
            break;
        case 'control':
            this.isCtrlPressed = false;
            break;
        case 'alt':
            this.isAltPressed = false;
            break;
    }
    for (const manager of this.managers) {
      if (manager.handleKeyUp) {
        manager.handleKeyUp(event);
      }
    }
  }

  private isClickOnToolbar(event: MouseEvent): boolean {
    if (!this.toolbar) return false;

    const rect = this.canvas!.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    return this.toolbar.isPointInside(x, y);
  }

  private handleToolbarClick(event: MouseEvent): void {
    if (!this.toolbar) return;

    const rect = this.canvas!.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    this.toolbar.handleClick(x, y);
  }

  @HostListener('window:resize')
  onResize() {
    this.resizeCanvas();

    if (this.validationPanel) {
      this.validationPanel.setPosition(
        (this.canvas!.width - this.validationPanel.getWidth()) / 2,
        60
      );
    }
  }

  private initializeCanvas() {
    this.canvas = document.querySelector('.designer-canvas') as HTMLCanvasElement;
    if (this.canvas) {
      this.ctx = this.canvas.getContext('2d');
      this.resizeCanvas();
      if (this.ctx) {
        this.drawDesignSurface();
      }
      window.addEventListener('resize', () => this.resizeCanvas());
    }
    this.elementToolbox = new ElementToolbox(this, this.canvas, this.parkingSpaceTypesService);
  }

  private drawDesignSurface() {
    if (!this.ctx || !this.canvas) return;

    const invScale = 1 / this.scale;
    const visibleWidth = this.canvas.width * invScale;
    const visibleHeight = this.canvas.height * invScale;

    // Set background color to light grey
    this.ctx.fillStyle = '#f0f0f0';
    this.ctx.fillRect(-this.offsetX * invScale, -this.offsetY * invScale, visibleWidth, visibleHeight);

    // Draw checkerboard pattern
    const squareSize = 5; // 5-unit spacing
    const startX = Math.floor(-this.offsetX * invScale / squareSize) * squareSize;
    const startY = Math.floor(-this.offsetY * invScale / squareSize) * squareSize;
    const endX = startX + visibleWidth + squareSize;
    const endY = startY + visibleHeight + squareSize;

    for (let x = startX; x < endX; x += squareSize) {
      for (let y = startY; y < endY; y += squareSize) {
        // Determine if this square should be filled (creates checkerboard pattern)
        const isAlternate = (Math.floor(x / squareSize) + Math.floor(y / squareSize)) % 2 === 0;
        this.ctx.fillStyle = isAlternate ? '#f0f0f0' : '#e8e8e8'; // Two very light shades of grey
        this.ctx.fillRect(x, y, squareSize, squareSize);
      }
    }

    // Draw origin marker (grey cross)
    this.ctx.save();
    this.ctx.strokeStyle = '#999999'; // Medium grey color
    this.ctx.lineWidth = 1 / this.scale;
    
    // Draw horizontal line
    this.ctx.beginPath();
    this.ctx.moveTo(-20 / this.scale, 0);
    this.ctx.lineTo(20 / this.scale, 0);
    this.ctx.stroke();
    
    // Draw vertical line
    this.ctx.beginPath();
    this.ctx.moveTo(0, -20 / this.scale);
    this.ctx.lineTo(0, 20 / this.scale);
    this.ctx.stroke();
    
    // Add small circle at intersection
    this.ctx.beginPath();
    this.ctx.arc(0, 0, 3 / this.scale, 0, Math.PI * 2);
    this.ctx.stroke();
    
    this.ctx.restore();
  }

  private createToolbar() {
    if (this.canvas) {
      // Center the toolbar horizontally
      const toolbarX = this.canvas.width + this.TOOLBAR_GAP*2 - (this.canvas.width - this.TOOLBAR_WIDTH);
      this.toolbar = new Toolbar(this, this.canvas);
      this.toolbar.setPosition(toolbarX, this.TOOLBAR_Y);
      this.toolbar.setDimensions(this.canvas.width - this.TOOLBAR_GAP*4 - this.TOOLBAR_WIDTH*2.3, this.TOOLBAR_HEIGHT);
      
      // Create toggle tools group
      const toggleGroup = this.toolbar.addGroup('Toggles');
      toggleGroup.addTool(new ToggleMapmodeTool(this.canvas!, this, toggleGroup));
      toggleGroup.addTool(new LevelDesignerLockTool(this.canvas!, this, toggleGroup));
      toggleGroup.addTool(new ToggleMeasurementsTool(this.canvas!, this, toggleGroup));
      toggleGroup.addTool(new ToggleSnappingTool(this.canvas!, this, toggleGroup));

      // Create action tools group
      const actionGroup = this.toolbar.addGroup('Actions');
      actionGroup.addTool(new UndoTool(this.canvas!, this, actionGroup));
      actionGroup.addTool(new CloneTool(this.canvas!, this, actionGroup));

      // Create alignment tools group
      const alignmentGroup = this.toolbar.addGroup('Align');
      alignmentGroup.addTool(new AlignLeftTool(this.canvas!, this, alignmentGroup));
      alignmentGroup.addTool(new AlignCenterHorizontalTool(this.canvas!, this, alignmentGroup));
      alignmentGroup.addTool(new AlignRightTool(this.canvas!, this, alignmentGroup));
      alignmentGroup.addTool(new AlignTopTool(this.canvas!, this, alignmentGroup));
      alignmentGroup.addTool(new AlignMiddleVerticalTool(this.canvas!, this, alignmentGroup));
      alignmentGroup.addTool(new AlignBottomTool(this.canvas!, this, alignmentGroup));

      // Create compress tools group
      const compressGroup = this.toolbar.addGroup('Compress');
      compressGroup.addTool(new CompressHorizontalTool(this.canvas!, this, compressGroup));
      compressGroup.addTool(new CompressVerticalTool(this.canvas!, this, compressGroup));


      this.toolbar.onToolActivated.subscribe((tool: Tool) => {
        this.activeTool = tool;
      });

      // Position the element toolbox on the left
      this.elementToolbox = new ElementToolbox(this, this.canvas, this.parkingSpaceTypesService);
      this.elementToolbox.setPosition(this.TOOLBAR_GAP, this.TOOLBAR_Y);
      this.elementToolbox.setDimensions(this.TOOLBAR_WIDTH, this.TOOLBAR_HEIGHT);

      // Position the property editor on the right
      this.propertyEditor = new PropertyEditor(this, this.canvas, this.organizationsService, this.apiService);
      this.propertyEditor.setPosition(this.canvas.width - this.TOOLBAR_WIDTH*1.3 - this.TOOLBAR_GAP, this.TOOLBAR_Y);
      this.propertyEditor.setDimensions(this.TOOLBAR_WIDTH*1.3, this.TOOLBAR_HEIGHT);
    }
  }

  private resizeCanvas() {
    if (this.canvas) {
      this.canvas.width = this.canvas.offsetWidth;
      this.canvas.height = this.canvas.offsetHeight;

      // Update positions when canvas is resized
      if (this.toolbar && this.elementToolbox && this.propertyEditor) {
        const toolbarX = (this.canvas.width - this.TOOLBAR_WIDTH) / 2;
        this.toolbar.setPosition(toolbarX, this.TOOLBAR_Y);
        this.propertyEditor.setPosition(this.canvas.width - this.TOOLBAR_WIDTH - this.TOOLBAR_GAP, this.TOOLBAR_Y);
      }

      this.redraw();
    }
  }

  public redraw() {
    if (!this.ctx || !this.canvas) return;
    const startTime = performance.now();
    
    // Clear the canvas
    this.ctx.setTransform(1, 0, 0, 1, 0, 0);
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    // Apply scaling and offset for the design surface
    this.ctx.setTransform(this.scale, 0, 0, this.scale, this.offsetX, this.offsetY);

    // Draw the appropriate content based on mode
    if(this.MapMode == false){
      this.drawDesignSurface();
    }
    else{
      this.drawMap();
    }
    this.drawElements();
    
    // Call OnDraw method for each manager
    for (const manager of this.managers) {
      manager.OnDraw();
    }

    // Reset transform for UI elements
    this.ctx.setTransform(1, 0, 0, 1, 0, 0);
    
    // Draw UI elements
    this.toolbar?.Refresh();
    this.elementToolbox?.Refresh();

    // Draw any new elements that are being dragged last and on top of everything else
    if(this.elementToolbox?.draggedElement !== null) {
      // Reapply the world transform before drawing the dragged element
      this.ctx.setTransform(this.scale, 0, 0, this.scale, this.offsetX, this.offsetY);
      
      if (this.elementToolbox?.draggedElement instanceof PolygonElement) {
        const points = this.elementToolbox.draggedElement.getPoints();
        if (points.length > 0) {
          console.log('Dragged new element first point:', { x: points[0].x, y: points[0].y });
        }
      }
      this.elementToolbox?.draggedElement.draw(this.ctx);
    }

    // Draw active tool overlays
    if (this.toolbar) {
      this.ctx.setTransform(1, 0, 0, 1, 0, 0); // Reset transform for tool rendering
      const activeTools = this.toolbar.getActiveTools();
      activeTools.forEach(tool => tool.render(this.ctx!));
    }

    // Draw property editor after toolbar
    this.propertyEditor?.draw();

    if (this.validationPanel) {
      this.validationPanel.draw();
    }

    const endTime = performance.now();
  }


  private drawElements(): void {
    if (!this.ctx) return;
    // Draw elements
    this.elements.forEach(element => element.draw(this.ctx!));
    
    // Let active tools render their overlays
    if (this.toolbar) {
        const activeTools = this.toolbar.getActiveTools();
        activeTools.forEach(tool => tool.render(this.ctx!));
    }
    
    this.ctx.restore();
  }

  private drawMap(): void {
    if (!this.ctx || !this.canvas) return;
    
    // Clear the canvas with a light background
    this.ctx.fillStyle = '#f0f0f0';
    this.ctx.fillRect(-this.offsetX / this.scale, -this.offsetY / this.scale, 
                     this.canvas.width / this.scale, this.canvas.height / this.scale);
    
    if (!this.parkingLevel) {
      this.ctx.fillStyle = '#000000';
      this.ctx.font = '16px Arial';
      this.ctx.fillText('No parking level found', 20, 30);
      return;
    }
    
    // Get GeoLocationPoints from the form
    const geoLocationPoints = this.parkingLevel.formGroup.get('GeoLocationPoints')?.value;
    
    if (!geoLocationPoints || geoLocationPoints.length === 0) {
      // If no geo location is set, show an error message
      this.ctx.fillStyle = '#000000';
      this.ctx.font = '16px Arial';
      this.ctx.fillText('No geo location set, using default', 20, 30);
    }
    
    // Calculate zoom level based on current scale
    // We use the same scale as in design view to maintain consistent element sizes
    const baseZoom = 15; // Increase base zoom level for better quality (was 14)
    const zoomOffset = Math.log2(this.scale);
    const zoomLevel = Math.max(15, Math.min(19, Math.floor(baseZoom + zoomOffset)));
    
    // Load and draw map tiles (these will be drawn first, as the background)
    this.drawMapTiles(this.ctx, zoomLevel);
    
    
    // Draw elements in map mode (these will be drawn on top of the map tiles)
    
    // Draw debug information at the bottom of the map
    this.drawMapDebugInfo(zoomLevel);
  }
  
  private drawMapTiles(ctx: CanvasRenderingContext2D, zoomLevel: number): void {
    if (!this.ctx || !this.canvas || !this.parkingLevel) {
      console.error('Cannot draw map tiles: missing context, canvas, or parking level');
      return;
    }
    
    // Get GeoLocationPoints from the parking level
    const geoLocationPoints = this.parkingLevel.formGroup.get('GeoLocationPoints')?.value;
    if (!geoLocationPoints || geoLocationPoints.length === 0) {
      console.error('Cannot draw map tiles: no geo location points available');
      return;
    }
    
    
    // Get rotation if available
    const rotation = -this.parkingLevel.formGroup.get('GeoRotation')?.value || 0;

    // Standard tile size in pixels (at zoom level 0, the entire world is 256x256 pixels)
    const baseTileSize = 256;
    
    // Extract the longitude and latitude from geoLocationPoints
    let longitude, latitude;
    
    try {
      if (Array.isArray(geoLocationPoints) && geoLocationPoints.length > 0) {
        if (Array.isArray(geoLocationPoints)) {
          // Format: [[longitude, latitude], ...]
          longitude = Number(geoLocationPoints[0]);
          latitude = Number(geoLocationPoints[1]);
        } else if (typeof geoLocationPoints[0] === 'object') {
          // Format: [{longitude/lng, latitude/lat}, ...]
          const point = geoLocationPoints[0] as any;
          longitude = Number(point.longitude || point.lng);
          latitude = Number(point.latitude || point.lat);
        }
      }
      
      // If we couldn't extract valid coordinates, log an error and return
      if (longitude === undefined || latitude === undefined || isNaN(longitude) || isNaN(latitude)) {
        console.error('Invalid geo coordinates:', geoLocationPoints);
        return;
      }
      
      // Ensure coordinates are within valid range for Mercator projection
      longitude = Math.max(-180, Math.min(180, longitude));
      latitude = Math.max(-85.05112878, Math.min(85.05112878, latitude));
      
    } catch (error) {
      console.error('Error extracting coordinates:', error);
      return;
    }

    // Adjust the zoom level based on the current scale
    // When zoomed out far, use a lower zoom level but draw tiles larger
    let effectiveZoomLevel = zoomLevel;
    let tileScaleFactor = 1;
    
    // If zoomed out far (scale < 0.1), use a lower zoom level
    if (this.scale < 0.05) {
      // Use zoom level 13 when very zoomed out (was 10)
      effectiveZoomLevel = 13;
      // Calculate how many zoom levels we're adjusting by
      const zoomLevelDiff = zoomLevel - effectiveZoomLevel;
      // Each zoom level is 2x the size, so scale by 2^diff
      tileScaleFactor = Math.pow(2, zoomLevelDiff);
    } else if (this.scale < 0.2) {
      // Use zoom level 14 when moderately zoomed out (was 12)
      effectiveZoomLevel = 14;
      const zoomLevelDiff = zoomLevel - effectiveZoomLevel;
      tileScaleFactor = Math.pow(2, zoomLevelDiff);
    }
    

    // Calculate the size of a tile in meters at the current latitude and zoom level
    // Earth's circumference at the equator is 40,075,016.686 meters
    const earthCircumference = 40075016.686; // meters
    
    // Calculate the size of a tile in meters at the current latitude and zoom level
    // At the equator, the circumference is 40,075,016.686 meters
    // As we move away from the equator, the east-west distance gets smaller by cos(latitude)
    const metersPerTileAtEquator = earthCircumference / Math.pow(2, effectiveZoomLevel);
    const metersPerTileAtLatitude = metersPerTileAtEquator * Math.cos(latitude * Math.PI / 180);
    
    // Calculate the tile size in our world units (meters)
    // In our level designer, 1 unit = 1 meter
    // Apply the tile scale factor to make tiles larger when using a lower zoom level
    const tileSizeInMeters = metersPerTileAtLatitude * tileScaleFactor;
    

    // Calculate tile coordinates
    const centerTileX = this.longToTile(longitude, effectiveZoomLevel);
    const centerTileY = this.latToTile(latitude, effectiveZoomLevel);


    // Check if tile coordinates are valid
    if (isNaN(centerTileX) || isNaN(centerTileY)) {
      console.error('Invalid tile coordinates:', { centerTileX, centerTileY });
      return;
    }
    
    // Calculate the floored tile coordinates
    const centerTileXFloored = Math.floor(centerTileX);
    const centerTileYFloored = Math.floor(centerTileY);
    
    // Calculate offsets within the center tile
    const centerTileXOffset = centerTileX - centerTileXFloored;
    const centerTileYOffset = centerTileY - centerTileYFloored;
    
    // Calculate how many tiles to load in each direction based on canvas size
    // We need to calculate this based on the actual size of tiles in meters
    // When using a lower zoom level, we need fewer tiles to cover the same area
    const tilesX = Math.ceil(this.canvas.width / (tileSizeInMeters * this.scale)) + 2;
    const tilesY = Math.ceil(this.canvas.height / (tileSizeInMeters * this.scale)) + 2;


    // Calculate the range of tiles to load
    const startTileX = centerTileXFloored - Math.floor(tilesX / 2);
    const startTileY = centerTileYFloored - Math.floor(tilesY / 2);
    const endTileX = startTileX + tilesX;
    const endTileY = startTileY + tilesY;
    
    // Calculate the center of the canvas in pixels
    const canvasCenterX = this.canvas.width / 2;
    const canvasCenterY = this.canvas.height / 2;
    
    // Draw a placeholder background
    this.ctx.fillStyle = '#e0e0e0';
    this.ctx.fillRect(-this.offsetX / this.scale, -this.offsetY / this.scale, 
                    this.canvas.width / this.scale, this.canvas.height / this.scale);
    
    // Draw all tiles
    for (let x = startTileX; x <= endTileX; x++) {
      for (let y = startTileY; y <= endTileY; y++) {
        // Create a cache key for this tile
        const tileKey = `${effectiveZoomLevel}_${x}_${y}`;
        
        // Calculate the position to draw the tile in meters
        // The position is relative to the center (0,0) in our world coordinates
        const tileOffsetX = (x - centerTileXFloored - centerTileXOffset) * tileSizeInMeters;
        const tileOffsetY = (y - centerTileYFloored - centerTileYOffset) * tileSizeInMeters;
        
        // Check if the tile is already in the cache
        if (LevelDesignerComponent.tileCache.has(tileKey)) {
          const img = LevelDesignerComponent.tileCache.get(tileKey)!;
          
          // Only draw if the image is fully loaded
          if (img.complete && img.naturalWidth > 0) {
            this.drawSingleTile(img, tileOffsetX, tileOffsetY, canvasCenterX, canvasCenterY, tileSizeInMeters);
          }
        } else {
          // Tile not in cache, need to load it
          
          // Create image for the tile
          const img = new Image();
          img.crossOrigin = 'anonymous';
          
          // Set up the onload handler before setting the src
          img.onload = () => {
            // Store in cache
            LevelDesignerComponent.tileCache.set(tileKey, img);
            
            // Request a redraw to show the newly loaded tile
            this.redraw();
          };
          
          // Handle loading errors
          img.onerror = () => {
            console.error(`Failed to load tile at zoom ${effectiveZoomLevel}, x=${x}, y=${y}`);
            // Remove from cache to allow retry
            LevelDesignerComponent.tileCache.delete(tileKey);
          };
          
          // Set the source URL for the tile
          const mapTilerKey = 'lMLywyNGGLa77fIqg5SR';
          const tileUrl = `https://api.maptiler.com/tiles/satellite-v2/${effectiveZoomLevel}/${x}/${y}.jpg?key=${mapTilerKey}`;
          img.src = tileUrl;
          
          // Store in cache immediately to prevent duplicate loads
          LevelDesignerComponent.tileCache.set(tileKey, img);
        }
      }
    }
    
    // Draw a marker at the center point (0,0)
    // This is the fixed point that should always be at the geo coordinates
    this.ctx.save();
    this.ctx.fillStyle = 'red';
    this.ctx.beginPath();
    this.ctx.arc(0, 0, 5 / this.scale, 0, Math.PI * 2);
    this.ctx.fill();
    
    // Add a crosshair to make the center more visible
    this.ctx.strokeStyle = 'white';
    this.ctx.lineWidth = 1 / this.scale;
    this.ctx.beginPath();
    this.ctx.moveTo(-10 / this.scale, 0);
    this.ctx.lineTo(10 / this.scale, 0);
    this.ctx.moveTo(0, -10 / this.scale);
    this.ctx.lineTo(0, 10 / this.scale);
    this.ctx.stroke();
    this.ctx.restore();
  }

  // Helper method to draw a single tile with inverse rotation
  private drawSingleTile(img: HTMLImageElement, tileOffsetX: number, tileOffsetY: number, 
                        canvasCenterX: number, canvasCenterY: number, tileSizeInMeters: number): void {
    if (!this.ctx) {
      return;
    }
    
    // Get rotation from the parking level
    const rotation = this.parkingLevel?.formGroup.get('GeoRotation')?.value || 0;
    
    // Apply inverse rotation (negative of the level's rotation)
    const inverseRotation = -rotation;
    
    // Save the current state
    this.ctx.save();
    
    // Apply inverse rotation if needed
    if (inverseRotation !== 0) {
      // Rotate around the origin (0,0) point in world coordinates
      // This ensures the map rotates around the same point as the level elements
      this.ctx.translate(0, 0);
      this.ctx.rotate((inverseRotation * Math.PI) / 180);
      this.ctx.translate(0, 0);
    }
    
    try {
      // Draw the tile at its correct size in meters
      // Since we're already in the scaled context, we use the tile size in meters directly
      // This ensures the tile is drawn at the correct scale relative to our level elements
      this.ctx.drawImage(img, tileOffsetX, tileOffsetY, tileSizeInMeters, tileSizeInMeters);
    } catch (error) {
      console.error('Error drawing tile:', error);
    }
    
    // Restore the transformation
    this.ctx.restore();
  }

  public setOffsetX(offsetX: number) {
    if(!isNaN(offsetX))
      this.offsetX = offsetX;
  }
  public setOffsetY(offsetY: number) {
    if(!isNaN(offsetY))
      this.offsetY = offsetY;
  }
  public getOffsetX() {
    return this.offsetX;
  }
  public getOffsetY() {
    return this.offsetY;
  }

  public getScale() {
    return this.scale;
  }

  public setScale(scale: number) {
    if(isNaN(scale))
      scale = 1;
    this.scale = scale;
  }

  public setSelectedElements(elements: LevelDesignerElementBase[]): void {
    // Deselect previous elements
    this.selectedElements.forEach(element => {
      element.selected = false;
    });
    
    this.selectedElements = elements;
    this.selectionChanged.emit(this.selectedElements);
    this.toolbar?.notifyToolsOfSelectionChange(this.selectedElements);
    this.stateChanged.emit(); // Emit state changed event when selection changes
  }

  public saveState() {
    for (const element of this.selectedElements) {
      element.updateForm();
    }

    if (this._levelData) {
      const snapshot = this.cloneFormValue(this._levelData.getRawValue());

      const top = this.undoStack[this.undoStack.length - 1];
      if (top === undefined || JSON.stringify(top) !== JSON.stringify(snapshot)) {
        this.undoStack.push(snapshot);

        // Cap the history (baseline + last 10 user actions).
        if (this.undoStack.length > this.maxUndoStackSize) {
          this.undoStack.shift();
        }
      }

      this._levelData.markAsDirty();
      this._levelData.markAsTouched();
      // Emit the change
      this.levelDataChange.emit(this._levelData);
    }

    // Emit state changed event
    this.stateChanged.emit();
  }

  // Reset the undo history to a single baseline snapshot of the current form value.
  private resetUndoStack() {
    this.undoStack = this._levelData
      ? [this.cloneFormValue(this._levelData.getRawValue())]
      : [];
  }

  public undo() {
    if (this.undoStack.length <= 1 || !this._levelData) {
      return;
    }

    this.undoStack.pop(); // Discard the current state
    const previousState = this.undoStack[this.undoStack.length - 1];

     this.applyFormValue(this._levelData, this.cloneFormValue(previousState));
    this.reloadElements(false);

    this.setSelectedElements([]);
    this.redraw();
  }

  private cloneFormValue(value: any): any {
    return structuredClone(value);
  }

  private applyFormValue(control: AbstractControl, value: any): void {
    if (control instanceof FormArray) {
      const items: any[] = Array.isArray(value) ? value : [];
      while (control.length > items.length) {
        control.removeAt(control.length - 1);
      }
      items.forEach((itemValue, index) => {
        if (index < control.length) {
          this.applyFormValue(control.at(index), itemValue);
        } else {
          control.push(this.buildControlFromValue(itemValue));
        }
      });
    } else if (control instanceof FormGroup) {
      const obj = value && typeof value === 'object' ? value : {};
      Object.keys(obj).forEach(key => {
        const child = control.get(key);
        if (child) {
          this.applyFormValue(child, obj[key]);
        } else {
          control.addControl(key, this.buildControlFromValue(obj[key]));
        }
      });
      Object.keys(control.controls).forEach(key => {
        if (!(key in obj)) {
          control.removeControl(key);
        }
      });
    } else {
      control.setValue(value ?? null);
    }
  }

  private buildControlFromValue(value: any): AbstractControl {
    if (Array.isArray(value)) {
      const hasObjectItems = value.some(
        v => v !== null && typeof v === 'object' && !Array.isArray(v)
      );
      if (hasObjectItems || value.length === 0) {
        const formArray = new FormArray<AbstractControl>([]);
        value.forEach(itemValue => formArray.push(this.buildControlFromValue(itemValue)));
        return formArray;
      }
      return new FormControl(value);
    }
    if (value !== null && typeof value === 'object') {
      const formGroup = new FormGroup({});
      Object.keys(value).forEach(key =>
        formGroup.addControl(key, this.buildControlFromValue(value[key]))
      );
      return formGroup;
    }
    return new FormControl(value);
  }

  public reloadElements(resetHistory: boolean = true) {
    if (!this._levelData || !this._levelData.controls['PolygonPoints']) {
        // Create default empty level with dimensions
        const defaultLevel = new FormGroup({
            PolygonPoints: new FormArray([
                new FormControl([0, 0]),
                new FormControl([0.1, 0]), 
                new FormControl([0.1, 0.05]),
                new FormControl([0, 0.05])
            ]),
            Rows: new FormArray([]),
            Gates: new FormArray([]),
            GatewayConfigurations: new FormArray([]),
            MapItems: new FormArray([]),
            Controllers: new FormArray([]),
            SignConfigurations: new FormArray([]),
            CarCounterConfigurations: new FormArray([]),
            ParkingMeterConfigurations: new FormArray([])
        });
        this._levelData = defaultLevel;
    }
    if(this._levelData.controls['PolygonPoints']?.value?.length === 0)
    {
      this._levelData.controls['PolygonPoints'].setValue([[0, 0], [0.1, 0], [0.1, 0.05], [0, 0.05]]);
    }

    // Ensure Controllers FormArray exists
    if (!this._levelData.contains('Controllers')) {
        this._levelData.addControl('Controllers', new FormArray([]));
    }

    // Ensure SignConfigurations FormArray exists
    if (!this._levelData.contains('SignConfigurations')) {
        this._levelData.addControl('SignConfigurations', new FormArray([]));
    }

    // Ensure CarCounterConfigurations FormArray exists
    if (!this._levelData.contains('CarCounterConfigurations')) {
        this._levelData.addControl('CarCounterConfigurations', new FormArray([]));
    }

    // Ensure ParkingMeterConfigurations FormArray exists
    if (!this._levelData.contains('ParkingMeterConfigurations')) {
        this._levelData.addControl('ParkingMeterConfigurations', new FormArray([]));
    }

    // Clear existing elements
    this.elements = [];

    // The elements being discarded wrap the previous FormGroup. Leaving them selected keeps the
    // property editor bound to a detached form - it would keep showing pre-reload values, and
    // saveState() would write element geometry into a form that is no longer submitted.
    if (this.selectedElements.length > 0) {
      this.setSelectedElements([]);
    }

    // Create new ParkingLevelElement
    const newParkingLevel = new ParkingLevelElement(this._levelData);
    this.parkingLevel = newParkingLevel;
    this.elements.push(newParkingLevel);

    // Load Rows and Spaces
    var rows = this._levelData.controls['Rows'] as FormArray;
    for (const row of rows.controls) {
        const newParkingRow = new ParkingRowElement(row as FormGroup);
        this.elements.push(newParkingRow);
        this.parkingLevel?.elements.push(newParkingRow);
        var spaces = row.get('Spaces') as FormArray;
        for (const space of spaces.controls) {
            const newParkingSpace = new ParkingSpaceElement(space as FormGroup, newParkingRow, this.parkingSpaceTypesService);
            this.elements.push(newParkingSpace);
            newParkingRow.elements.push(newParkingSpace);
        }
        var guidanceLights = row.get('GuidanceLightConfigurations') as FormArray;
        if (guidanceLights) {
            for (const light of guidanceLights.controls) {
                const newLight = new GuidanceLight(light as FormGroup);
                newLight.currentParentElement = newParkingRow; 
                this.elements.push(newLight);
                newParkingRow.elements.push(newLight);

                // Load port assignments
                const portAssignments = (light as FormGroup).get('PortAssignments') as FormArray;
                if (portAssignments) {
                    for (const portAssignment of portAssignments.controls) {
                        const portGroup = portAssignment as FormGroup;
                        const portNumber = portGroup.get('PortNumber')?.value;
                        const spaceId = portGroup.get('ParkingSpaceId')?.value;
                        
                        if (spaceId) {
                            // Find the corresponding parking space
                            const space = newParkingRow.elements.find(e => 
                                e instanceof ParkingSpaceElement && 
                                e.formGroup.get('Id')?.value === spaceId
                            ) as ParkingSpaceElement;
                            
                            if (space) {
                                newLight.updatePortAssignment(portNumber, space);
                            }
                        }
                    }
                }
            }
        }
    }

    // Load Gates and Lanes
    var gates = this._levelData.controls['Gates'] as FormArray;
    if (gates) {
        for (const gate of gates.controls) {
            const newGate = new Gate(gate as FormGroup);
            newGate.currentParentElement = this.parkingLevel;
            this.elements.push(newGate);
            this.parkingLevel?.elements.push(newGate);
           
            
            var lanes = gate.get('Lanes') as FormArray;
            if (lanes) {
                for (const lane of lanes.controls) {
                    const newLane = new Lane(lane as FormGroup);
                    newLane.currentParentElement = newGate;
                    this.elements.push(newLane);
                    newGate.elements.push(newLane);
                    var barriers = lane.get('Barriers') as FormArray;
                    if (barriers) {
                        for (const barrier of barriers.controls) {
                            const newBarrier = new Barrier(barrier as FormGroup);
                            newBarrier.currentParentElement = newLane;
                            this.elements.push(newBarrier);
                            newLane.elements.push(newBarrier);
                        }
                    }
                    var cameras = lane.get('Cameras') as FormArray;
                    if (cameras) {
                        for (const camera of cameras.controls) {
                            const newCamera = new Camera(camera as FormGroup);
                            newCamera.currentParentElement = newLane;
                            this.elements.push(newCamera);
                            newLane.elements.push(newCamera);
                        }
                    }
                    
                    var trafficLights = lane.get('TrafficLights') as FormArray;
                    if (trafficLights) {
                        for (const light of trafficLights.controls) {
                            const newTrafficLight = new TrafficLight(light as FormGroup);
                            newTrafficLight.currentParentElement = newLane;
                            this.elements.push(newTrafficLight);
                            newLane.elements.push(newTrafficLight);
                        }
                    }
                }
            }
        }
    }

    // Load Gateways
    var gateways = this._levelData.controls['GatewayConfigurations'] as FormArray;
    if (gateways) {
        for (const gateway of gateways.controls) {
            const newGateway = new Gateway(gateway as FormGroup);
            newGateway.currentParentElement = this.parkingLevel;
            this.elements.push(newGateway);
            this.parkingLevel?.elements.push(newGateway);
        }
    }

    // Load Controllers
    var controllers = this._levelData.controls['Controllers'] as FormArray;
    if (controllers) {
        for (const controller of controllers.controls) {
            const newController = new Controller(controller as FormGroup);
            newController.currentParentElement = this.parkingLevel;
            this.elements.push(newController);
            // Find a lane that contains this controller
            const lane = this.elements.find(element => 
                element instanceof Lane && 
                element.isPointInside(newController.x, newController.y)
            );

            if (lane instanceof PolygonElement) {
                lane.elements.push(newController);
            } else {
                this.parkingLevel?.elements.push(newController);
            }
        }
    }

    // Load Signs
    var signs = this._levelData.controls['SignConfigurations'] as FormArray;
    if (signs) {
        for (const sign of signs.controls) {
            const newSign = new Sign(sign as FormGroup);
            newSign.currentParentElement = this.parkingLevel;
            this.elements.push(newSign);
            this.parkingLevel?.elements.push(newSign);
        }
    }
    
    // Load Car Counters
    var carCounters = this._levelData.controls['CarCounterConfigurations'] as FormArray;
    if (carCounters) {
        for (const counter of carCounters.controls) {
            const newCounter = new CarCounter(counter as FormGroup);
            newCounter.currentParentElement = this.parkingLevel;
            this.elements.push(newCounter);
            this.parkingLevel?.elements.push(newCounter);
        }
    }
    
    // Load Parking Meters
    var parkingMeters = this._levelData.controls['ParkingMeterConfigurations'] as FormArray;
    if (parkingMeters) {
        for (const meter of parkingMeters.controls) {
            const newMeter = new ParkingMeter(meter as FormGroup);
            newMeter.currentParentElement = this.parkingLevel;
            this.elements.push(newMeter);
            this.parkingLevel?.elements.push(newMeter);
        }
    }

    // Load all other elements from MapItems
    const mapItems = this._levelData.get('MapItems') as FormArray;
    if (mapItems) {
        for (const item of mapItems.controls) {
            const itemType = item.get('Type')?.value;
            const constructor = this.elementConstructors.get(itemType?.toLowerCase());
            if (constructor) {
                const element = constructor(item as FormGroup);
                element.currentParentElement = this.parkingLevel;
                this.elements.push(element);
                this.parkingLevel?.elements.push(element);
              } else {
                console.warn(`Unknown map item type: ${itemType}`);
            }
        }
    }
    
    
    // Sort elements once after loading
    this.sortElementsByLayer();
    // Set level designer reference on point elements
    this.elements.forEach(element => {
        if (element instanceof PointElement) {
            element.setLevelDesigner(this);
        }
    });
    // Calculate bounding box of all elements
    const boundingBox = this.calculateBoundingBox();

    if (boundingBox && this.canvas) {
        // Calculate the scale to fit the bounding box in two-thirds of the view
        const canvasWidth = this.canvas!.width;
        const canvasHeight = this.canvas!.height;
        const targetWidth = canvasWidth * (2/3);
        const targetHeight = canvasHeight * (2/3);

        const scaleX = targetWidth / boundingBox.width;
        const scaleY = targetHeight / boundingBox.height;
        const newScale = Math.min(scaleX, scaleY);

        // Set the new scale
        this.setScale(newScale);

        // Calculate new offset to center the elements
        const centerX = boundingBox.x + boundingBox.width / 2;
        const centerY = boundingBox.y + boundingBox.height / 2;
        this.offsetX = canvasWidth / 2 - centerX * newScale;
        this.offsetY = canvasHeight / 2 - centerY * newScale;
    }

    if (resetHistory) {
      this.resetUndoStack();
    }
    this.redraw();
  }

  private calculateBoundingBox(): { x: number, y: number, width: number, height: number } | null {
    if (this.elements.length === 0) {
      return null;
    }

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    this.elements.forEach(element => {
      if (element instanceof ParkingLevelElement) {
        const elementBoundingBox = element.getBoundingBox();
        minX = Math.min(minX, elementBoundingBox.x);
        minY = Math.min(minY, elementBoundingBox.y);
        maxX = Math.max(maxX, elementBoundingBox.x + elementBoundingBox.width);
        maxY = Math.max(maxY, elementBoundingBox.y + elementBoundingBox.height);
      }
    });

    return {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY
    };
    
  }

  private isClickOnElementToolbox(event: MouseEvent): boolean {
    if (!this.elementToolbox) return false;

    const rect = this.canvas!.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    return this.elementToolbox.isPointInside(x, y);
  }

  private handleElementToolboxClick(event: MouseEvent): void {
    if (!this.elementToolbox) return;

    const rect = this.canvas!.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    this.elementToolbox.handleMouseDown(event);
  }

  /**
   * Converts mouse event coordinates to world coordinates taking into account
   * canvas position, scale, and offset
   */
  public getWorldCoordinates(event: MouseEvent): { x: number, y: number } {
    const canvasRect = this.canvas!.getBoundingClientRect();
    return {
      x: (event.clientX - canvasRect.left - this.offsetX) / this.scale,
      y: (event.clientY - canvasRect.top - this.offsetY) / this.scale
    };
  }

  public sortElementsByLayer(): void {
    // Sort in descending order (highest to lowest)
    this.elements.sort((a, b) => a.layerIndex - b.layerIndex);
  }

  private getCanvasCoordinates(event: MouseEvent): { x: number, y: number } {
    const canvasRect = this.canvas!.getBoundingClientRect();
    return {
      x: event.clientX - canvasRect.left,
      y: event.clientY - canvasRect.top
    };
  }

  public addElement(element: LevelDesignerElementBase): void {
    this.elements.push(element);
    
    // If the element has a parent that is a PolygonElement, add it to the parent's elements array
    if (element.currentParentElement instanceof PolygonElement) {
        element.currentParentElement.elements.push(element);
    }
    
    this.sortElementsByLayer();

    this._levelData?.markAsDirty();
    this.saveState();
    //this.redraw();
    this.validate();
  }

  public getAllElements(): LevelDesignerElementBase[] {
    return this.elements;
  }

  ngOnDestroy(): void {
    window.removeEventListener('keydown', this.handleKeyDown.bind(this));
    window.removeEventListener('keyup', this.handleKeyUp.bind(this));
  }

  // Add public getter for propertyEditor
  public getPropertyEditor(): PropertyEditor | null {
    return this.propertyEditor;
  }

  // Custom implementations of tile coordinate calculations
  private longToTile(longitude: number, zoom: number): number {
    return ((longitude + 180) / 360) * Math.pow(2, zoom);
  }

  private latToTile(latitude: number, zoom: number): number {
    const latRad = latitude * Math.PI / 180;
    return ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * Math.pow(2, zoom);
  }

  // New methods for map dragging functionality
  private startMapDragging(event: MouseEvent): void {
    if (!this.parkingLevel) {
      console.error('Cannot start map dragging: no parking level found');
      return;
    }
    
    this.isMapDragging = true;
    this.canvas!.style.cursor = 'move';
    
    // Store the starting position
    const coords = this.getCanvasCoordinates(event);
    this.mapDragStartX = coords.x;
    this.mapDragStartY = coords.y;
    
    
    // Get current geo coordinates
    const geoLocationPoints = this.parkingLevel.formGroup.get('GeoLocationPoints')?.value;
    if (!geoLocationPoints || geoLocationPoints.length === 0) {
      console.error('Cannot start map dragging: no geo location points available');
      this.isMapDragging = false;
      return;
    }
    
    
    try {
      // Extract the longitude and latitude
      // GeoLocationPoints is a simple array [longitude, latitude]
      this.mapDragStartLongitude = Number(geoLocationPoints[0]);
      this.mapDragStartLatitude = Number(geoLocationPoints[1]);
      
      // Validate the extracted coordinates
      if (isNaN(this.mapDragStartLongitude) || isNaN(this.mapDragStartLatitude)) {
        console.error('Invalid coordinates extracted:', { 
          lon: this.mapDragStartLongitude, 
          lat: this.mapDragStartLatitude 
        });
        this.isMapDragging = false;
        return;
      }
      
      // Ensure coordinates are within valid range for Mercator projection
      this.mapDragStartLongitude = Math.max(-180, Math.min(180, this.mapDragStartLongitude));
      this.mapDragStartLatitude = Math.max(-85.05112878, Math.min(85.05112878, this.mapDragStartLatitude));
      
      
    } catch (error) {
      console.error('Error extracting coordinates for map dragging:', error);
      this.isMapDragging = false;
    }
  }

  private updateMapDragging(event: MouseEvent): void {
    if (!this.isMapDragging || !this.parkingLevel) {
      return;
    }
    
    const coords = this.getCanvasCoordinates(event);
    const deltaX = coords.x - this.mapDragStartX;
    const deltaY = coords.y - this.mapDragStartY;
    
    
    // Get the rotation angle from the parking level
    const rotation = this.parkingLevel.formGroup.get('GeoRotation')?.value || 0;
    const rotationRad = (rotation * Math.PI) / 180;
    
    // Apply the rotation to the mouse movement delta
    // Use the level's rotation (not inverse) to match the coordinate system
    const rotatedDeltaX = deltaX * Math.cos(rotationRad) - deltaY * Math.sin(rotationRad);
    const rotatedDeltaY = deltaX * Math.sin(rotationRad) + deltaY * Math.cos(rotationRad);
    
    // Calculate the current zoom level
    const baseZoom = 14; // Base zoom level for scale = 1.0
    const zoomOffset = Math.log2(this.scale);
    const zoomLevel = Math.max(14, Math.min(19, Math.floor(baseZoom + zoomOffset)));
    
    // Calculate the size of a tile in meters at the current latitude
    const earthCircumference = 40075016.686; // meters
    const metersPerTileAtEquator = earthCircumference / Math.pow(2, zoomLevel);
    const metersPerTileAtLatitude = metersPerTileAtEquator * Math.cos(this.mapDragStartLatitude * Math.PI / 180);
    
    // Calculate how much to move the geo coordinates
    // At zoom level z, the world is 2^z tiles wide, each 256 pixels
    const worldWidth = Math.pow(2, zoomLevel) * 256;
    const worldHeight = Math.pow(2, zoomLevel) * 256;
    
    // Convert pixel movement to geo coordinate movement
    // Moving right (positive deltaX) should decrease longitude (map moves left)
    // Moving down (positive deltaY) should decrease latitude (map moves up)
    const lonPerPixel = 180 / worldWidth;
    const latPerPixel = 170 / worldHeight; // Approximate range of latitude in Mercator projection
    
    // Apply the deltas with correct signs and scale by zoom level
    // For longitude: moving right (positive rotatedDeltaX) decreases longitude
    // For latitude: moving down (positive rotatedDeltaY) decreases latitude
    const zoomFactor = Math.pow(2, -(zoomLevel - baseZoom));
    const lonDelta = -rotatedDeltaX * lonPerPixel * zoomFactor;
    const latDelta = rotatedDeltaY * latPerPixel * zoomFactor;
    
    
    // Calculate new coordinates
    const newLongitude = this.mapDragStartLongitude + lonDelta;
    const newLatitude = this.mapDragStartLatitude + latDelta;
    
    // Create a new array with updated coordinates
    // GeoLocationPoints is a simple array [longitude, latitude]
    const updatedPoints = [newLongitude, newLatitude];
    
    // Update the form control
    const geoLocationPointsControl = this.parkingLevel.formGroup.get('GeoLocationPoints');
    if (geoLocationPointsControl) {
      geoLocationPointsControl.setValue(updatedPoints);
      geoLocationPointsControl.markAsDirty();
      geoLocationPointsControl.updateValueAndValidity();
    } else {
      console.error('GeoLocationPoints control not found in form');
    }
    
    // Force a redraw to show the updated position
    this.redraw();
  }

  private endMapDragging(): void {
    if (this.isMapDragging) {
      this.isMapDragging = false;
      this.canvas!.style.cursor = 'default';
      
      // Save the state to record the change
      this.saveState();
    }
  }

  // Method to draw map interaction instructions
  private drawMapDebugInfo(zoomLevel: number): void {
    if (!this.ctx || !this.canvas || !this.parkingLevel) return;
    
    // Save current transform
    this.ctx.save();
    
    // Reset transform to draw in screen coordinates
    this.ctx.setTransform(1, 0, 0, 1, 0, 0);
    
    // Create instructions text
    const instructions = [
      'Map Mode',
      'CTRL + Left Mouse: Move map position',
      'SHIFT + Left Mouse: Rotate map',
    ];
    
    // Draw background for instructions
    const padding = 10;
    const lineHeight = 20;
    const textWidth = 300;
    const textHeight = instructions.length * lineHeight + padding * 2;
    
    const ctx = this.ctx!;
    const canvas = this.canvas!;
    
    // Position the instructions at the bottom center of the canvas
    const boxX = canvas.width / 2 - textWidth / 2;
    const boxY = canvas.height - textHeight - 10;
    
    // Create gradient for 3D effect similar to toolbar
    const gradient = ctx.createLinearGradient(0, boxY, 0, boxY + textHeight);
    gradient.addColorStop(0, '#E8E8E8');
    gradient.addColorStop(1, '#C8C8C8');
    
    // Draw rounded rectangle with padding
    ctx.fillStyle = gradient;
    this.roundRect(boxX, boxY, textWidth, textHeight, 5);
    ctx.fill();
    
    // Add highlight for embossed effect
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
    ctx.lineWidth = 2;
    this.roundRect(boxX + 1, boxY + 1, textWidth - 2, textHeight - 2, 4);
    ctx.stroke();
    
    // Add shadow for embossed effect
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.2)';
    this.roundRect(boxX, boxY, textWidth, textHeight, 5);
    ctx.stroke();
    
    // Draw instructions text
    ctx.fillStyle = '#333333'; // Dark text color
    ctx.font = 'bold 14px Arial';
    ctx.textAlign = 'left';
    
    // Draw the title with bold font
    ctx.fillText(
      instructions[0],
      boxX + padding,
      boxY + padding + 14
    );
    
    // Draw the rest of the instructions with regular font
    ctx.font = '14px Arial';
    for (let i = 1; i < instructions.length; i++) {
      ctx.fillText(
        instructions[i],
        boxX + padding,
        boxY + padding + i * lineHeight + 14
      );
    }
    
    // Restore original transform
    ctx.restore();
  }
  
  // Helper method to draw rounded rectangles (similar to toolbar)
  private roundRect(x: number, y: number, width: number, height: number, radius: number) {
    if (!this.ctx) return;
    
    this.ctx.beginPath();
    this.ctx.moveTo(x + radius, y);
    this.ctx.arcTo(x + width, y, x + width, y + height, radius);
    this.ctx.arcTo(x + width, y + height, x, y + height, radius);
    this.ctx.arcTo(x, y + height, x, y, radius);
    this.ctx.arcTo(x, y, x + width, y, radius);
    this.ctx.closePath();
  }

  // New methods for map rotation functionality
  private startMapRotating(event: MouseEvent): void {
    if (!this.parkingLevel) {
      console.error('Cannot start map rotation: no parking level found');
      return;
    }
    
    this.isMapRotating = true;
    this.canvas!.style.cursor = 'crosshair';
    
    // Store the starting position
    const coords = this.getCanvasCoordinates(event);
    this.mapRotateStartX = coords.x;
    this.mapRotateStartY = coords.y;
    
    
    // Get current rotation
    this.mapRotateStartAngle = this.parkingLevel.formGroup.get('GeoRotation')?.value || 0;
  }

  private updateMapRotation(event: MouseEvent): void {
    if (!this.isMapRotating || !this.parkingLevel) {
      return;
    }
    
    const coords = this.getCanvasCoordinates(event);
    
    // Calculate the center of the canvas
    const canvasCenterX = this.canvas!.width / 2;
    const canvasCenterY = this.canvas!.height / 2;
    
    // Calculate the angle between the starting drag point, the center, and the current mouse position
    const startAngle = Math.atan2(this.mapRotateStartY - canvasCenterY, this.mapRotateStartX - canvasCenterX);
    const currentAngle = Math.atan2(coords.y - canvasCenterY, coords.x - canvasCenterX);
    
    // Calculate the angle difference in degrees
    let angleDelta = (currentAngle - startAngle) * (180 / Math.PI);
    
    // Calculate the new rotation angle
    let newRotation = this.mapRotateStartAngle + angleDelta;
    
    // Normalize to 0-360 degrees
    newRotation = ((newRotation % 360) + 360) % 360;
    
    
    // Update the form control
    const geoRotationControl = this.parkingLevel.formGroup.get('GeoRotation');
    if (geoRotationControl) {
      geoRotationControl.setValue(newRotation);
      geoRotationControl.markAsDirty();
      geoRotationControl.updateValueAndValidity();
    } else {
      console.error('GeoRotation control not found in form');
    }
  }

  private endMapRotation(): void {
    if (this.isMapRotating) {
      this.isMapRotating = false;
      this.canvas!.style.cursor = 'default';
      
      // Save the state to record the change
      this.saveState();
    }
  }

  public validate(): void {
    // Clear previous validation errors
    this.validationErrors.clear();

    // Validate all elements
    this.elements.forEach(element => {
      const validationResult = element.checkPropertiesValid(this.elements);
      if (validationResult.errors.length > 0) {
        this.validationErrors.set(element, validationResult.errors);
      }
    });

    // Update validation panel with new errors
    if (this.validationPanel) {
      this.validationPanel.updateErrors(this.validationErrors);
    }
  }

  public getValidationErrors(): Map<LevelDesignerElementBase, string[]> {
    return this.validationErrors;
  }

  public hasValidationErrors(): boolean {
    return this.validationErrors.size > 0;
  }

  public getLastMouseEvent(): MouseEvent | null {
    return this.lastMouseEvent;
  }

  private isDoubleClick(x: number, y: number, currentTime: number): boolean {
    if (this.lastMouseDownTime === 0) return false;
    const timeDiff = currentTime - this.lastMouseDownTime;
    const distance = Math.sqrt(
      Math.pow(x - this.lastMouseDownX, 2) + 
      Math.pow(y - this.lastMouseDownY, 2)
    );

    // Consider it a double click if:
    // 1. Time difference is less than DOUBLE_CLICK_DELAY
    // 2. Distance moved is less than 5 pixels (to account for slight hand movement)
    return timeDiff < this.DOUBLE_CLICK_DELAY && distance < 5;
  }

  private handleDoubleClick(event: MouseEvent): MouseEventResult | undefined {
    const coords = this.getCanvasCoordinates(event);
    const worldCoords = this.getWorldCoordinates(event);

    // Try toolbar first
    if (this.toolbar?.handleDoubleClick) {
      const result = this.toolbar.handleDoubleClick(coords.x, coords.y);
      if (result?.stopPropagation) {
        return result;
      }
    }

    // Try element toolbox
    if (this.elementToolbox?.handleDoubleClick) {
      const result = this.elementToolbox.handleDoubleClick(coords.x, coords.y);
      if (result?.stopPropagation) {
        return result;
      }
    }

    // Try property editor
    if (this.propertyEditor?.handleDoubleClick) {
      const result = this.propertyEditor.handleDoubleClick(coords.x, coords.y);
      if (result?.stopPropagation) {
        return result;
      }
    }

    // Try validation panel
    if (this.validationPanel?.handleDoubleClick) {
      const result = this.validationPanel.handleDoubleClick(coords.x, coords.y);
      if (result?.stopPropagation) {
        return result;
      }
    }

    // Try all elements
    for (const element of this.elements) {
      if (element.handleDoubleClick && element.handleDoubleClick(worldCoords.x, worldCoords.y)) {
        this.redraw();
        return { cursor: 'default', stopPropagation: true, redraw: true };
      }
    }

    // Try all managers
    for (const manager of this.managers) {
      if (manager.handleDoubleClick) {
        const result = manager.handleDoubleClick(event);
        if (result?.stopPropagation) {
          return result;
        }
      }
    }

    return undefined;
  }
}
